import {
  Component,
  computed,
  EventEmitter,
  inject,
  input,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { IndicatorStore } from '../../../stores/indicator/indicator.state';
import { Indicator } from '../../../models/indicator';
import { DividerModule } from 'primeng/divider';
import { IndicatorFilterComponent } from '../indicator-filter/indicator-filter.component';
import { SharedModule } from 'primeng/api';
import { TooltipModule } from 'primeng/tooltip';
import { CategoryStore } from '../../../stores/category/category.state';
import { units } from '../../../models/unit';
import { ListboxModule } from 'primeng/listbox';
import { FormsModule } from '@angular/forms';
import { buildSortedIndicatorList } from '../../../stores/indicator/indicator.selectors';
import { PickListHeaderComponent } from '../../atoms/pick-list-header/pick-list-header.component';
import { TagSummaryPipe } from '../../../pipes/tag-summary.pipe';

/**
 * Single selection of an indicator set, filterable via `app-indicator-filter`.
 */
@Component({
  selector: 'app-indicator-set-pick-list',
  standalone: true,
  imports: [
    DividerModule,
    IndicatorFilterComponent,
    SharedModule,
    TooltipModule,
    ListboxModule,
    FormsModule,
    PickListHeaderComponent,
    TagSummaryPipe,
  ],
  templateUrl: './indicator-set-pick-list.component.html',
  styleUrl: './indicator-set-pick-list.component.scss',
})
export class IndicatorSetPickListComponent implements OnInit {
  /** Preselected set, e.g. when navigating back to this step. */
  @Input() selectedSet: Indicator | null = null;
  /** Sets which can't be chosen, e.g. because they are part of the current selection. */
  readonly disabledIds = input<string[]>([]);
  /** Emits the chosen set, or `null` when the selection was removed. */
  @Output() indicatorSelection = new EventEmitter<Indicator | null>();

  readonly indicatorStore = inject(IndicatorStore);
  readonly categoryStore = inject(CategoryStore);

  /**
   * Sets matching the filter, including sets with at least one matching child,
   * sorted by category tree.
   */
  private readonly sets = computed(() =>
    buildSortedIndicatorList(
      this.categoryStore.asArray(),
      this.indicatorStore
        .filteredItems()
        .filter(indicator => indicator.isSet || indicator.parent),
      {},
      this.indicatorStore.onlySets()
    ).filter(indicator => indicator.isSet)
  );

  /** Listbox options; wrapped because `optionDisabled` expects a field name. */
  readonly options = computed(() => {
    const disabledIds = new Set(this.disabledIds());
    return this.sets().map(set => ({
      set,
      disabled: disabledIds.has(set._id),
    }));
  });

  /**
   * Number of children of the given set.
   *
   * @param {Indicator} set - The indicator set.
   * @return {number} The child count.
   */
  childCount(set: Indicator): number {
    return this.indicatorStore.allGroupedChildren()[set._id]?.length ?? 0;
  }

  /**
   * Short unit label (symbol, falling back to the name) of the given indicator.
   *
   * @param {Indicator} indicator - The indicator.
   * @return {string} The unit label or an empty string.
   */
  unitLabel(indicator: Indicator): string {
    const unit = units[indicator.unit];
    return unit ? unit.symbol || unit.name : '';
  }

  /**
   * Drops a preselected set which became unavailable (e.g. after changing the selection).
   */
  ngOnInit(): void {
    if (this.selectedSet && this.disabledIds().includes(this.selectedSet._id)) {
      this.selectedSet = null;
      this.selectSet();
    }
  }

  /**
   * Emits the currently selected set (or `null`) using the `indicatorSelection` event.
   *
   * @return {void} Does not return a value.
   */
  selectSet(): void {
    this.indicatorSelection.emit(this.selectedSet ?? null);
  }
}
